/**
 * Deterministic screening engine.
 *
 * Pipeline: normalize text → split into sections (by headings, fallback by keywords)
 * → score every category with weighted markers and numeric rules → generate Czech notes
 * and summary from templates. The same input always yields the same output; no AI is used.
 */
import { CATEGORIES, Category, CategoryScores, Evidence, ExtractedMetrics, ScreeningResult } from "./types";
import { CATEGORY_KEYWORDS, HEADING_WORDS, MARKERS, RED_FLAGS } from "./markers";
import { findMoney, fold, formatMoney, normalizeText, splitSentences, truncate } from "./text";
import { extractIdentity, extractMetrics } from "./extract";
import { computeFinalScore, clampScore, scoreBand } from "./finalScore";

export const ENGINE_VERSION = "rules-1.0";

/** Minimum amount of text needed for a meaningful screening. */
export const MIN_TEXT_LENGTH = 50;

export const CATEGORY_LABELS: Record<Category, string> = {
    vision: "Vize a strategie",
    finance: "Finanční zdraví",
    implementation: "Realizace",
    risks: "Řízení rizik",
    operations: "Provozní efektivita",
};

/** Starting score of a category that has at least some supporting text. */
const BASE_SCORE: Record<Category, number> = {
    vision: 45,
    finance: 40,
    implementation: 45,
    risks: 60,
    operations: 45,
};

/** Score of a category the document says nothing about. */
export const MISSING_SCORE = 15;

export class ScreeningError extends Error {}

interface Section {
    category: Category;
    start: number;
    end: number;
}

/** A heading starts a line / follows a double space (PDF layout), or is numbered ("3. Finanční rozbor"). */
const headingRegex = (words: RegExp) =>
    new RegExp(`(?:(?:^|\\n|\\s{2,})(?:\\d{1,2}\\.\\s*)?|\\d{1,2}\\.\\s*)${words.source}`, "g");

/** Finds section headings; a section runs until the next heading of any category. */
export function findSections(folded: string): Section[] {
    const starts: Array<{ category: Category; index: number }> = [];
    for (const category of CATEGORIES) {
        for (const m of Array.from(folded.matchAll(headingRegex(HEADING_WORDS[category])))) {
            starts.push({ category, index: m.index ?? 0 });
        }
    }
    starts.sort((a, b) => a.index - b.index);
    const deduped = starts.filter((s, i) => i === 0 || s.index - (starts[i - 1]?.index ?? -100) > 3);
    return deduped.map((s, i) => ({
        category: s.category,
        start: s.index,
        end: deduped[i + 1]?.index ?? folded.length,
    }));
}

/** Assigns sentences to categories: by section when the category has a heading, else by keywords. */
export function groupSentences(original: string): Record<Category, string[]> {
    const folded = fold(original);
    const sections = findSections(folded);
    const withHeading = new Set(sections.map((s) => s.category));
    const groups: Record<Category, string[]> = { vision: [], finance: [], implementation: [], risks: [], operations: [] };

    let cursor = 0;
    for (const sentence of splitSentences(original)) {
        const probe = sentence.slice(0, 20);
        const found = original.indexOf(probe, cursor);
        const position = found >= 0 ? found : cursor;
        if (found >= 0) cursor = found + 1;

        const section = sections.find((s) => position >= s.start && position < s.end);
        const foldedSentence = fold(sentence);
        for (const category of CATEGORIES) {
            if (section && section.category === category) {
                groups[category].push(sentence);
            } else if (!withHeading.has(category) && CATEGORY_KEYWORDS[category].test(foldedSentence)) {
                groups[category].push(sentence);
            }
        }
    }
    return groups;
}

function numericRules(category: Category, corpus: string, metrics: ExtractedMetrics): Evidence[] {
    const rules: Evidence[] = [];
    switch (category) {
        case "vision":
            if (/\d+(?:[.,]\d+)?\s?%[^.]{0,60}?\d+\s*(mesic|let|rok|tydn)|(do|behem)\s+(konce\s+)?(roku\s+)?20\d\d/.test(corpus)) {
                rules.push({ label: "měřitelný cíl", weight: 8 });
            }
            break;
        case "finance":
            if (metrics.amountsFound > 0) rules.push({ label: "vyčíslené částky", weight: 4 });
            if (metrics.revenue !== null) rules.push({ label: "uvedený obrat", weight: 3 });
            if (metrics.roiMonths !== null) {
                const m = metrics.roiMonths;
                if (m <= 12) rules.push({ label: `návratnost do 12 měsíců (${m})`, weight: 12 });
                else if (m <= 24) rules.push({ label: `návratnost do 2 let (${m} měs.)`, weight: 8 });
                else if (m <= 36) rules.push({ label: `návratnost do 3 let (${m} měs.)`, weight: 3 });
                else if (m > 48) rules.push({ label: `dlouhá návratnost (${m} měs.)`, weight: -6 });
            }
            if (metrics.growthPercent !== null) {
                const g = metrics.growthPercent;
                if (g > 0) rules.push({ label: `růst ${g} %`, weight: Math.min(12, Math.round(g / 2) + 2) });
                else if (g < 0) rules.push({ label: `pokles ${Math.abs(g)} %`, weight: Math.max(-15, Math.round(g / 2) - 2) });
            }
            if (metrics.profit !== null && metrics.profit < 0) rules.push({ label: "záporný výsledek hospodaření", weight: -8 });
            if (metrics.equity !== null && metrics.assets !== null && metrics.assets > 0) {
                const ratio = metrics.equity / metrics.assets;
                const pct = Math.round(ratio * 100);
                if (ratio >= 0.5) rules.push({ label: `vysoký podíl vlastního kapitálu (${pct} %)`, weight: 10 });
                else if (ratio >= 0.3) rules.push({ label: `přiměřený podíl vlastního kapitálu (${pct} %)`, weight: 5 });
                else if (ratio < 0.1) rules.push({ label: `nízký podíl vlastního kapitálu (${pct} %)`, weight: -10 });
            }
            break;
        case "implementation":
            if (metrics.phases !== null) {
                if (metrics.phases >= 3) rules.push({ label: `${metrics.phases} fáze realizace`, weight: 6 });
                else if (metrics.phases === 2) rules.push({ label: "2 fáze realizace", weight: 3 });
            }
            if (metrics.implementationWeeks !== null) {
                const w = metrics.implementationWeeks;
                if (w > 0 && w <= 52) rules.push({ label: `realistický harmonogram (${w} týdnů)`, weight: 4 });
                else if (w > 104) rules.push({ label: `velmi dlouhá realizace (${w} týdnů)`, weight: -4 });
            }
            break;
        case "risks":
            if (metrics.riskProbabilityPercent !== null) {
                const p = metrics.riskProbabilityPercent;
                if (p <= 10) rules.push({ label: `nízká pravděpodobnost rizik (${p} %)`, weight: 4 });
                else if (p > 30) rules.push({ label: `vysoká pravděpodobnost rizik (${p} %)`, weight: -8 });
            }
            break;
        case "operations":
            break;
    }
    return rules;
}

const NEGATION_BEFORE = /\b(ne(ma|maji|mame|ni|jsou)|bez|chybi|zadn\w*)\b[^.,;]{0,25}$/;
const NEGATION_AFTER = /^[^.,;]{0,15}\b(neni|nejsou|chybi)\b/;

/** True when at least one occurrence of the marker is not negated ("strategie není stanovena", "bez úvěrů"). */
export function hasAffirmedMatch(re: RegExp, corpus: string): boolean {
    const global = new RegExp(re.source, re.flags.includes("g") ? re.flags : `${re.flags}g`);
    for (const m of Array.from(corpus.matchAll(global))) {
        const index = m.index ?? 0;
        // Markers that already express absence (e.g. "strategie není", "bez harmonogramu") are affirmed as they are.
        if (/^(bez|ne|chybi)|\b(neni|nejsou|chybi)\b/.test(m[0])) return true;
        const before = corpus.slice(Math.max(0, index - 40), index);
        const after = corpus.slice(index + m[0].length, index + m[0].length + 25);
        if (!NEGATION_BEFORE.test(before) && !NEGATION_AFTER.test(after)) return true;
    }
    return false;
}

export function scoreCategory(category: Category, sentences: string[], metrics: ExtractedMetrics) {
    if (sentences.length === 0) {
        return { score: MISSING_SCORE, evidence: [] as Evidence[], covered: false };
    }
    const corpus = fold(sentences.join(" "));
    const evidence: Evidence[] = MARKERS[category]
        .filter((marker) => hasAffirmedMatch(marker.re, corpus))
        .map(({ label, weight }) => ({ label, weight }));
    evidence.push(...numericRules(category, corpus, metrics));
    const score = clampScore(BASE_SCORE[category] + evidence.reduce((sum, e) => sum + e.weight, 0));
    return { score, evidence, covered: true };
}

const BAND_LABEL = { strong: "Silná stránka", average: "Průměrná úroveň", weak: "Slabé místo" } as const;

function formatEvidence(evidence: Evidence[]): string {
    if (evidence.length === 0) return "";
    const sorted = [...evidence].sort((a, b) => b.weight - a.weight);
    return ` Signály: ${sorted.map((e) => `${e.weight >= 0 ? "+" : "−"} ${e.label}`).join(", ")}.`;
}

function financeFacts(metrics: ExtractedMetrics): string {
    const facts: string[] = [];
    if (metrics.revenue !== null) facts.push(`obrat ${formatMoney(metrics.revenue)}`);
    if (metrics.profit !== null) {
        facts.push(metrics.profit < 0 ? `ztráta ${formatMoney(-metrics.profit)}` : `zisk ${formatMoney(metrics.profit)}`);
    }
    if (metrics.budget !== null) facts.push(`rozpočet ${formatMoney(metrics.budget)}`);
    if (metrics.roiMonths !== null) facts.push(`návratnost ${metrics.roiMonths} měsíců`);
    if (metrics.growthPercent !== null) facts.push(`${metrics.growthPercent >= 0 ? "růst" : "pokles"} ${Math.abs(metrics.growthPercent)} %`);
    return facts.length ? ` Klíčové údaje: ${facts.join(", ")}.` : "";
}

function buildNote(category: Category, score: number, covered: boolean, sentences: string[], evidence: Evidence[], metrics: ExtractedMetrics): string {
    if (!covered) {
        return "Dokument k této oblasti neobsahuje žádné informace – pro přesnější hodnocení doplňte podklady.";
    }
    const content = sentences
        .filter((s) => s.length > 25)
        .slice(0, 2)
        .map((s) => truncate(s, 200))
        .join(" ");
    const facts = category === "finance" ? financeFacts(metrics) : "";
    return `${BAND_LABEL[scoreBand(score)]} (${score} %). ${content}${facts}${formatEvidence(evidence)}`.replace(/\s+/g, " ").trim();
}

function pickPrice(metrics: ExtractedMetrics, foldedAll: string): string {
    if (metrics.revenue !== null) return `Obrat: ${formatMoney(metrics.revenue)}`;
    if (metrics.budget !== null) return `Rozpočet: ${formatMoney(metrics.budget)}`;
    const largest = findMoney(foldedAll).sort((a, b) => b.value - a.value)[0];
    return largest ? `Nejvyšší uvedená částka: ${formatMoney(largest.value, largest.currency)}` : "Nezjištěno";
}

function recommendation(score: number, redFlags: string[]): string {
    if (redFlags.length > 0) return "nedoporučeno – nalezena závažná rizika";
    if (score >= 70) return "doporučeno k další spolupráci";
    if (score >= 50) return "podmíněně doporučeno – ověřte slabá místa";
    return "nedoporučeno bez doplnění podkladů";
}

function buildSummary(score: number, scores: CategoryScores, coverage: Record<Category, boolean>, redFlags: string[]): string {
    const ranked = [...CATEGORIES].sort((a, b) => scores[b] - scores[a]);
    const best = ranked[0] as Category;
    const worst = ranked[ranked.length - 1] as Category;
    const missing = CATEGORIES.filter((c) => !coverage[c]).map((c) => CATEGORY_LABELS[c].toLowerCase());
    const parts = [
        `Celkové skóre ${score} % – ${recommendation(score, redFlags)}.`,
        `Nejsilnější oblast: ${CATEGORY_LABELS[best]} (${scores[best]} %), nejslabší oblast: ${CATEGORY_LABELS[worst]} (${scores[worst]} %).`,
    ];
    if (missing.length) parts.push(`Chybějící podklady: ${missing.join(", ")}.`);
    if (redFlags.length) parts.push(`Varování: ${redFlags.join(", ")}.`);
    parts.push(`Hodnocení vzniklo pravidlovým screeningem (${ENGINE_VERSION}) bez použití AI.`);
    return parts.join(" ");
}

/** Scores a document text. Throws ScreeningError when the text is too short. */
export function screenDocument(rawText: string, fileName: string): ScreeningResult {
    const original = normalizeText(rawText ?? "");
    if (original.replace(/\s/g, "").length < MIN_TEXT_LENGTH) {
        throw new ScreeningError(`Dokument neobsahuje dostatek textu pro screening (méně než ${MIN_TEXT_LENGTH} znaků).`);
    }
    const foldedAll = fold(original);
    const groups = groupSentences(original);
    const metrics = extractMetrics(foldedAll, fold(groups.implementation.join(" ")));
    const identity = extractIdentity(original, fileName);

    const scores = {} as CategoryScores;
    const notes = {} as Record<Category, string>;
    const evidence = {} as Record<Category, Evidence[]>;
    const coverage = {} as Record<Category, boolean>;

    for (const category of CATEGORIES) {
        const result = scoreCategory(category, groups[category], metrics);
        scores[category] = result.score;
        evidence[category] = result.evidence;
        coverage[category] = result.covered;
        notes[category] = buildNote(category, result.score, result.covered, groups[category], result.evidence, metrics);
    }

    const redFlags = RED_FLAGS.filter((flag) => flag.re.test(foldedAll)).map((flag) => flag.label);
    const score = computeFinalScore(CATEGORIES.map((c) => scores[c]));

    return {
        ...identity,
        score,
        price: pickPrice(metrics, foldedAll),
        scores,
        notes,
        analysis_finance: buildSummary(score, scores, coverage, redFlags),
        evidence,
        coverage,
        redFlags,
        metrics,
        engineVersion: ENGINE_VERSION,
    };
}
